// @flow

import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import find from 'lodash/find';
import { Row, Col } from '@riseart/antd-provider';
import { Wrapper } from '@riseart/layout';
import { Link, Heading, MediaQuery } from '@riseart/common';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { Avatar } from 'shared_components/common/avatar/Avatar';
import { Banner, getBannerMedia } from 'shared_components/common/banner/Banner';
import { Share } from 'shared_components/common/share/Share';
import { Breadcrumbs } from 'shared_components/common/breadcrumbs/Breadcrumbs';
import { CollectionDescription } from 'shared_components/collections/containers/description/Description';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';
import { collection } from 'Enum';
import { components as COMPONENTS_CONFIG } from 'Config';

import { raScreenXsMax, raScreenSm } from '@riseart/antd-provider/dist/website/variables.less';
import {
  collectionInfoCls,
  collectionBannerCls,
  collectionBannerTopCls,
  collectionBannerBottomCls,
  collectionBannerTitleCls,
} from 'shared_components/collections/containers/info//Info.less';

const { fallbackImages: FALLBACK_IMAGES_LIST } = COMPONENTS_CONFIG.collections;

type Props = {
  id: number,
  image: Array<*>,
  title: string,
  description: string,
  avatarImages: Object,
  authorName: string,
  biography: string,
  authorUrl?: ?string,
};

/**
 * CollectionInfo
 *
 * @param props
 */
export const CollectionInfo = (props: Props) => {
  const { formatMessage } = useIntl();
  const { id, title, image, authorName, description, avatarImages, authorUrl } = props;
  const images = find(image, (image) => image.type === collection.image.type.TYPE_BANNER_ORIGINAL);
  const sharedData = {
    id,
    name: title,
    image: generateSrcForCDN(image),
    url: LocationManager.get('href'),
  };

  return (
    <div className={collectionInfoCls}>
      <Banner
        gradient
        size="medium"
        className={collectionBannerCls}
        altTitle={title}
        artDirectionType="banner.fullWidth"
        artDirectionKey="common.banner"
        media={getBannerMedia(images, FALLBACK_IMAGES_LIST, {
          small: collection.image.type.TYPE_BANNER_SMALL,
          medium: collection.image.type.TYPE_BANNER_MEDIUM,
          large: collection.image.type.TYPE_BANNER_LARGE,
        })}
      >
        <div className={collectionBannerTopCls}>
          <Heading tag="h1" align="center" className={collectionBannerTitleCls}>
            {title}
          </Heading>
        </div>
        <Row type="flex" align="end" justify="space-between" className={collectionBannerBottomCls}>
          <Col sm={3} />
          <Col xs={21} sm={18}>
            <Avatar theme="white" align="center" images={avatarImages} name={authorName}>
              <MediaQuery maxWidth={raScreenXsMax}>
                <FormattedMessage
                  id="components.collections.curator_text_short"
                  values={{
                    name: authorUrl ? (
                      <Link type="strong" to={authorUrl} title={authorName}>
                        {authorName}
                      </Link>
                    ) : (
                      <strong>{authorName}</strong>
                    ),
                  }}
                />
              </MediaQuery>
              <MediaQuery minWidth={raScreenSm}>
                <FormattedMessage
                  id="components.collections.curator_text"
                  values={{
                    name: authorUrl ? (
                      <Link type="strong" to={authorUrl} title={authorName}>
                        {authorName}
                      </Link>
                    ) : (
                      <strong>{authorName}</strong>
                    ),
                    curatorText: '',
                  }}
                />
              </MediaQuery>
            </Avatar>
          </Col>
          <Col xs={3} sm={3}>
            <Share
              theme="light"
              contentKey="collection_detail"
              contentValues={sharedData}
              image={sharedData.image}
              url={sharedData.url}
            />
          </Col>
        </Row>
      </Banner>
      <Wrapper>
        <Breadcrumbs
          links={[
            {
              name: formatMessage({ id: 'components.breadcrumbs.collection' }),
              url: UrlAssembler.collectionList(),
            },
            {
              isLink: false,
              name: title,
              url: LocationManager.get('originWithPath'),
            },
          ]}
        />
      </Wrapper>
      <Wrapper
        sizes={{
          xs: 'wide',
          sm: 'wide',
          md: 'wide',
          lg: 'narrow',
          xl: 'narrow',
        }}
      >
        <CollectionDescription>{description}</CollectionDescription>
      </Wrapper>
    </div>
  );
};
